import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { Button, Modal, message } from 'antd';
import { ExclamationCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { DELETE_EXTENSION, GET_EXTENSIONS } from 'gql/extensions';
import { stringSorter } from 'utils';
import useLoading from 'hooks/useLoading';
import { ProTable, DateDisplay } from 'components/layout';
import { ENUM_APPLICATION_STATUS } from 'constants/form';
import { useHasScopeTypeInProject, useProjectFrozen } from 'hooks';

const { confirm } = Modal;
const showConfirm = ({ onOk }) => {
  confirm({
    title: 'Confrim to Delete?',
    icon: <ExclamationCircleOutlined />,
    content: 'Do you want to delete this extension application?',
    onOk,
  });
};

const ExtensionsTable = ({ data, ...props }) => {
  const { projectId } = useParams();
  const projectFrozen = useProjectFrozen(projectId);
  const [deleteExtension, { loading: deleteLoading }] = useMutation(
    DELETE_EXTENSION,
    {
      refetchQueries: [
        {
          query: GET_EXTENSIONS,
          variables: {
            projectIds: [projectId],
            status: ['DRAFT', 'PENDING', 'REJECTED', 'APPROVED', 'VOIDED'],
          },
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        message.success('Extension deleted successfully');
      },
      onError: () => {},
    }
  );
  const { hasScopeType } = useHasScopeTypeInProject('WRITE_EXTENSION');
  useLoading(deleteLoading);
  const onOk = ({ id, projectId }) => {
    deleteExtension({
      variables: {
        input: {
          id,
          projectId,
        },
      },
    });
  };

  const columns = [
    {
      title: 'Application Date',
      key: 'date',
      sorter: (a, b) => a.date - b.date,
      render: ({ id, date }) => (
        <Link to={`/projects/${projectId}/extensions/${id}`}>
          <DateDisplay date={date} />
        </Link>
      ),
    },
    {
      title: 'New Completed Date',
      key: 'newCompletionDate',
      sorter: (a, b) => a.newCompletionDate - b.newCompletionDate,
      render: ({ id, newCompletionDate }) => (
        <Link to={`/projects/${projectId}/extensions/${id}`}>
          <DateDisplay date={newCompletionDate} />
        </Link>
      ),
    },
    {
      title: 'Justification',
      key: 'justification',
      dataIndex: 'justification',
      ellipsis: true,
      sorter: (a, b) => stringSorter(a.justification, b.justification),
      render: (_, { id }) => (
        <Link to={`/projects/${projectId}/extensions/${id}`}>{_}</Link>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      filters: true,
      onFilter: true,
      valueEnum: ENUM_APPLICATION_STATUS,
      sorter: (a, b) => stringSorter(a.status, b.status),
      render: (_, item) => (
        <Link to={`/projects/${projectId}/extensions/${item.id}`}>{_}</Link>
      ),
    },
    hasScopeType && {
      title: 'Action',
      key: 'action',
      hideInTable: projectFrozen || !hasScopeType,
      render: ({ id, status }) =>
        status === 'DRAFT' ? (
          <Button
            danger
            type="primary"
            onClick={() => {
              showConfirm({
                onOk: () => {
                  onOk({ id, projectId });
                },
              });
            }}
          >
            <DeleteOutlined />
            Delete
          </Button>
        ) : (
          ''
        ),
    },
  ];

  return (
    <ProTable
      columns={columns}
      dataSource={data.extensions}
      pagination={false}
      search={false}
      toolBarRender={false}
      rowKey={({ id }) => id}
      {...props}
    />
  );
};

ExtensionsTable.defaultProps = {
  data: {},
};

ExtensionsTable.propTypes = {
  data: PropTypes.object.isRequired,
};

export default ExtensionsTable;
